// Runtime env access; see window.__mindtrip_setup__ for client-side injection.
import { isClientSide } from "@/lib/is-client-side"
import { isServerSide } from "@/lib/is-server-side"

declare global {
  interface Window {
    __mindtrip_setup__?: string
  }
}

type EnvKeys =
  | "ABUSE_PREVENTION_SECRET"
  | "ADMIN_BEARER_TOKEN"
  | "API_SERVER_SECRET"
  | "API_SERVER_URL"
  | "APPLE_SIGN_IN_KEY_ID"
  | "APPLE_SIGN_IN_PK"
  | "APPLE_TEAM_ID"
  | "BASIC_AUTH_PASSWORD"
  | "BASIC_AUTH_USER"
  | "CSP_POLICY"
  | "DISABLE_BASIC_AUTH"
  | "ENABLE_IMPRESSIONS"
  | "ENABLE_IOS_WEB_VIEW"
  | "FACEBOOK_CLIENT_ID"
  | "FACEBOOK_SECRET"
  | "FORCE_SSL"
  | "GITHUB_CLIENT_ID"
  | "GITHUB_SECRET"
  | "GOOGLE_CLIENT_ID"
  | "GOOGLE_SECRET"
  | "HEROKU_APP_NAME"
  | "HEROKU_RELEASE_VERSION"
  | "HUBSPOT_ACCESS_TOKEN"
  | "RENDER_SERVICE_NAME"
  | "INSTAGRAM_CLIENT_ID"
  | "INSTAGRAM_SECRET"
  | "LOCAL_PROD"
  | "MT_AUTH_SECRET"
  | "MT_AUTH_SALT"
  | "MT_JWT_KEY"
  | "NEXTAUTH_URL"
  | "MT_DATA_REGION"
  | "NODE_ENV"
  | "REDIS_CACHE_TLS_URL"
  | "REDIS_CACHE_URL"
  | "REDIS_DB_TLS_URL"
  | "REDIS_DB_URL"
  | "HIGH_VALUE_CRAWLER_RPM"
  | "LOW_VALUE_CRAWLER_RPM"
  | "SPINOUT_BLOCKING_ENABLED"
  | "SPINOUT_THRESHOLD"
  | "SPINOUT_WINDOW_SECONDS"
  | "TESTER_BASIC_AUTH_PASSWORD"
  | "TESTER_BASIC_AUTH_USER"
  | "TRUSTED_PROXIES"
  | "UNIT_TEST"
  | "PRESS_FEED_URL"
  | "PRESS_FEED_SECRET"

  // client side
  | "NEXT_COOKIE_AUTH_DOMAIN"
  | "NEXT_PUBLIC_ALLOW_GOOGLE_MAPS_LIST_IMPORT"
  | "NEXT_PUBLIC_AMPLITUDE_API_KEY"
  | "NEXT_PUBLIC_AMPLITUDE_END_POINTS"
  | "NEXT_PUBLIC_AMPLITUDE_SERVER_ZONE"
  | "NEXT_PUBLIC_API_SERVER_URL"
  | "NEXT_PUBLIC_AUTH_V2"
  | "NEXT_PUBLIC_CF_INVISIBLE_TURNSTILE_SITE_KEY"
  | "NEXT_PUBLIC_CF_TURNSTILE_SITE_KEY"
  | "NEXT_PUBLIC_CF_TURNSTILE_MODE"
  | "NEXT_PUBLIC_DDB_DEBUG"
  | "NEXT_PUBLIC_HOTEL_API_DEBUG"
  | "NEXT_PUBLIC_DEVELOPMENT"
  | "NEXT_PUBLIC_ENABLE_FOLIO_DOCUMENT"
  | "NEXT_PUBLIC_GOOGLE_PLACES_API_KEY"
  | "NEXT_PUBLIC_HONEYBADGER_API_KEY"
  | "NEXT_PUBLIC_HONEYBADGER_ENV"
  | "NEXT_PUBLIC_CF_IMAGE_URL"
  | "NEXT_PUBLIC_CF_CGI_URL"
  | "NEXT_PUBLIC_ENABLE_HUBSPOT_INTEGRATION"
  | "NEXT_PUBLIC_GOOGLE_CLIENT_ID"
  | "NEXT_PUBLIC_HUBSPOT_FORM_ID"
  | "NEXT_PUBLIC_HUBSPOT_PORTAL_ID"
  | "NEXT_PUBLIC_GTM_ACCOUNTS"
  | "NEXT_PUBLIC_WEGLOT_API_KEY"
  | "NEXT_PUBLIC_EMBEDDED_CF_TURNSTILE_REQUIRED"
  | "NEXT_PUBLIC_CREATE_ANON_CF_TURNSTILE_REQUIRED"
  | "NEXT_PUBLIC_DOMAIN_URL"
  | "NEXT_PUBLIC_APPLE_SIGN_IN_SERVICE_ID"
  | "NEXT_PUBLIC_TUI_DOMAIN"
  | "NEXT_PUBLIC_NUITEE_WL_DOMAIN"
  | "NEXT_PUBLIC_RELEASE_VERSION"
  | "NEXT_PUBLIC_MICROSITE_ORGIDS"

const envValues = new Map<string, string>()
let loadInit = false

function loadEnv() {
  if (window.__mindtrip_setup__) {
    try {
      const parsed = JSON.parse(atob(window.__mindtrip_setup__))
      // make sure this looks alright
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        for (const key of Object.keys(parsed)) {
          if (
            (key.startsWith("NEXT_PUBLIC_") ||
              key === "NODE_ENV" ||
              key === "MT_DATA_REGION") &&
            typeof parsed[key] === "string"
          ) {
            envValues.set(key, parsed[key])
          }
        }
      }
    } catch {
      // noop, got bad json will act as if no env vars
    }
  }
}

function getWindowEnvVal(key: EnvKeys): string | undefined {
  if (!loadInit) {
    loadInit = true
    loadEnv()
  }

  return envValues.get(key) ?? undefined
}

// the only place we allow process.env to be used
export function env(key: EnvKeys): string | undefined {
  // eslint-disable-next-line no-process-env
  if (isServerSide() || process.env.UNIT_TEST === "true") {
    // eslint-disable-next-line no-process-env
    return process.env[key]
  }

  if (
    key.startsWith("NEXT_PUBLIC_") ||
    key === "NODE_ENV" ||
    key === "MT_DATA_REGION"
  ) {
    return getWindowEnvVal(key)
  }

  return undefined
}

// for dynamic server-side keys not in EnvKeys (e.g. per-org credentials)
export function envDynamic(key: string): string | undefined {
  // eslint-disable-next-line no-process-env
  return process.env[key]
}

export function buildEnv(): Record<string, string> {
  if (isClientSide()) {
    throw new Error("This function should only be called server-side")
  }

  // the only place we allow process.env to be used

  const values: Record<string, string> = {}

  // eslint-disable-next-line no-process-env
  for (const [key, value] of Object.entries(process.env)) {
    if (
      (key === "NODE_ENV" ||
        key === "MT_DATA_REGION" ||
        key.startsWith("NEXT_PUBLIC_")) &&
      value
    ) {
      values[key] = value
    }
  }

  return values
}
